import {
  ComponentPropsWithoutRef,
  forwardRef,
  useContext,
  useEffect,
} from "react";
import { StepperContext } from "./Stepper";
import styles from "./StepperPanel.module.scss";
import cx from "classnames";
import { LayoutUtilProps, Overflow } from "../Overflow";
import { useLayoutPropsUtil } from "../../hooks";
import { motion } from "motion/react";
import { core } from "@servicetitan/hammer-token";

/**
 * Props for the StepperFinalPanel component
 * @extends ComponentPropsWithoutRef<"div">
 * @extends LayoutUtilProps
 */
export type StepperFinalPanelProps = Omit<
  ComponentPropsWithoutRef<"div">,
  "onAnimationStart" | "onDragStart" | "onDragEnd" | "onDrag"
> &
  LayoutUtilProps;

/**
 * Final panel component displayed when all stepper steps are completed.
 *
 * Features:
 * - Displays only when all steps are completed
 * - Smooth fade-in animation on completion
 * - Automatic overflow handling with scroll support
 * - Accessible with proper ARIA roles
 * - Layout utilities for positioning and spacing
 * - Motion animations for smooth transitions
 * - Focus management for keyboard navigation
 * - Responsive design with flexible layout
 * - Context-based visibility control
 *
 * @example
 * <Stepper.FinalPanel>
 *   <h2>Congratulations!</h2>
 *   <p>You have successfully completed all steps.</p>
 *   <Button onClick={handleSubmit}>Submit</Button>
 * </Stepper.FinalPanel>
 */
export const StepperFinalPanel = forwardRef<
  HTMLDivElement,
  StepperFinalPanelProps
>((props, ref) => {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const { id, className, style, children, ...rest } = componentProps;
  const { allCompleted, setCurrentId } = useContext(StepperContext);

  useEffect(() => {
    if (allCompleted) {
      setCurrentId("final-panel");
    }
  }, [allCompleted, setCurrentId]);

  const tabPanelClassNames = cx(styles["stepper-panel"], className);
  const styleCombined = {
    ...style,
    ...layoutStyles,
  };

  if (allCompleted) {
    return (
      <motion.div
        style={styleCombined}
        className={tabPanelClassNames}
        transition={{
          opacity: {
            duration:
              +core.primitive.DurationSlow.value.replace("ms", "") / 1000,
          },
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        layout
        {...rest}
        role="tabpanel"
        data-anv="stepper-final-panel"
        tabIndex={0}
        ref={ref}
      >
        <Overflow>{children}</Overflow>
      </motion.div>
    );
  }
  return null;
});

StepperFinalPanel.displayName = "StepperFinalPanel";
